import pkceChallenge from "pkce-challenge";
import { motion } from "framer-motion";
import { startTransition, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { BASE_URL, STRAPI_BASE_URL } from "../../../api/baseURL";
import AppleStoreBadge from "../../../assets/images/Apple.svg";
import GooglePlayBadge from "../../../assets/images/Google.svg";
import Header from "../../../components/common/Header/Header";
import BackBtnWithLabel from "../../../components/common/backBtnWithLabel/backBtnWithLabel";
import ContinueBtn from "../../../components/common/eKey/continueBtn";
import Modal from "../../../components/common/modal/modal";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useScreenResolution from "../../../hooks/useScreenResolution";
import {
  clearLoginTokenInfo,
  loginWithBackupTokenInfo,
  selectLoginRegister,
} from "../../../redux/features/loginRegisterSlice";
import performLogout from "../../../utils/logoutHandler";
import GetVerifiedPage from "../GetVerifiedPage/GetVerifiedPage";
import AddressDetailsPage from "../addressDetails/addressDetailsPage";
import "./SigninPage.css";
import SignInForm from "./signInForm";

export const SIGN_IN_PAGE = "signInPage";
export const GET_VERIFIED_PAGE = "GetVerifiedPage";
// BenefitVerified now means "confirmed address on file" rather than "Wathiq
// case published" — the BENEFIT eKYC service is being retired.
export const ADDRESS_DETAILS_PAGE = "ADDRESS_DETAILS_PAGE";

const SignInPage = () => {
  const { mobile } = useScreenResolution();
  const [showModal, setShowModal] = useState(true);
  const [currentPage, setCurrentPage] = useState(SIGN_IN_PAGE);
  const [isEkeyLoading, setIsEkeyLoading] = useState(false);
  const [ekeyError, setEkeyError] = useState(false);

  const { t } = useTranslation();
  const translate = useLanguageTranslate();

  const loginRegisterData = useSelector(selectLoginRegister);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  function isPwa() {
    const displayModes = ["fullscreen", "standalone", "minimal-ui"];
    return displayModes.some(
      (displayMode) =>
        window.matchMedia(`(display-mode: ${displayMode})`).matches
    );
  }
  const generateAuthUrl = async () => {
    const challenge = await pkceChallenge();

    const standaloneDisplay =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    const isMobileApp = standaloneDisplay || !isPwa();

    // Save verifier
    localStorage.setItem("code_verifier", challenge.code_verifier);
    localStorage.setItem("code_challenge", challenge.code_challenge);

    // Prepare backend bridge URL
    const params = new URLSearchParams({
      code_challenge: challenge.code_challenge,
    });

    params.append("isMobile", isMobileApp);

    return `${BASE_URL}/ekey/redirect-to-ekey?${params.toString()}`;
  };

  const handleEkeyLogin = async () => {
    try {
      setIsEkeyLoading(true);
      setEkeyError(false);
      const url = await generateAuthUrl();
      // Redirect directly. A pre-flight `fetch(url, { mode: "no-cors" })`
      // does NOT work here because this URL responds with a 302 redirect to a
      // cross-origin auth provider, which opaque/no-cors fetches cannot
      // follow — the fetch throws and the redirect never happens.
      window.location.href = url;
    } catch (err) {
      console.error("Failed to redirect to eKey", err);
      setEkeyError(true);
    } finally {
      setIsEkeyLoading(false);
    }
  };

  // Redirect the user to the authorization URL
  const cbVerifyStatus = (data) => {
    if (data.Verified === false) {
      setCurrentPage(GET_VERIFIED_PAGE);
      dispatch(clearLoginTokenInfo());
    } else if (data.BenefitVerified === false) {
      setCurrentPage(ADDRESS_DETAILS_PAGE);
      dispatch(clearLoginTokenInfo());
    } else {
      // Set login success flag for travel policy window
      localStorage.setItem("loginSuccess", "true");

      // If this is a popup window (opened from travel policy), close it
      if (window.opener && window.opener !== window) {
        window.close();
      } else {
        navigate("/");
      }
    }
  };

  // Handle successful verification and address steps
  const verifedOnSuccess = () => {
    setCurrentPage(ADDRESS_DETAILS_PAGE);
  };

  const addressDetailsOnSuccess = () => {
    // Set login success flag for travel policy window
    localStorage.setItem("loginSuccess", "true");
    dispatch(loginWithBackupTokenInfo());

    // If this is a popup window (opened from travel policy), close it
    if (window.opener && window.opener !== window) {
      window.close();
    } else {
      navigate("/");
    }
  };

  const togglePages = () => {
    if (currentPage == GET_VERIFIED_PAGE) {
      return <GetVerifiedPage onSuccess={verifedOnSuccess} />;
    } else if (currentPage == SIGN_IN_PAGE) {
      return (
        <div className="sign_in_page flex align-center h_100vh justify-center overflow-y-auto">
          <div className="left_cont bg-[#00A9E7] h_100 max767:hidden lg:w-[29.5%] md:w-[25%]"></div>
          <div className="signinright_cont h_100 lg:px-20 lg:pb-4 md:px-12 md:pb-4 justify-center items-start flex lg:pt-[4%] md:pt-[3%] max767:p-0 max767:w-full max767:pt-[60px] max767:items-start lg:w-[70.5%] md:w-[75%]">
            <div className="form-division lg:w-[60%] md:w-[85%] max767:w-full max767:p-5">
              {mobile ? (
                <div className="content">
                  <BackBtnWithLabel
                    label={translate("Login", "تسجيل الدخول")}
                  />
                  <h1 className="text-[#1A2024] text-2xl not-italic font-semibold leading-8 max767:mb-5 max767:text-[28px] max767:font-semibold">
                    {translate(
                      "Welcome to Solidarity Online Services",
                      "مرحبًا بكم في خدمات سوليدرتي عبر الإنترنت"
                    )}
                  </h1>
                </div>
              ) : (
                <div className="content">
                  <h1 className="text-[#1A2024] text-2xl not-italic font-semibold leading-8">
                    {translate(
                      loginRegisterData?.sign_in_header?.title_en || "Welcome to Solidarity Online Services",
                      loginRegisterData?.sign_in_header?.title_ar || "مرحبًا بكم في خدمات سوليدرتي عبر الإنترنت"
                    )}
                  </h1>
                  <div className="flex align-center mt-3">
                    <p className="mb-3">{t("siginPage.new_to_label")} </p>
                    <Link
                      to="/sign-up"
                      className="text-[#00384D] text-base not-italic font-semibold leading-[26px] underline ml-1"
                    >
                      {t("siginPage.signup_link_label")}
                    </Link>
                  </div>
                </div>
              )}
              <ContinueBtn onClick={handleEkeyLogin} loading={isEkeyLoading} />
              <SignInForm cbVerifyStatus={cbVerifyStatus} />

              {!mobile && (
                <div className="downlaod_app_cont mt-6 w-full">
                  <p className="text-[#5B6871] text-sm not-italic font-normal leading-[150%] mx-0 my-2 flex">
                    {translate(
                      loginRegisterData?.download_app_title?.title_en || "Download our mobile app",
                      loginRegisterData?.download_app_title?.title_ar || "حمّل تطبيقنا على الهاتف"
                    )}
                  </p>
                  <div className="store_icons flex gap-2">
                    <a
                      href="https://apps.apple.com/bh/app/solidarity/id1585138622"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={loginRegisterData?.apple_store_icon?.img?.data?.attributes?.url
                          ? `${STRAPI_BASE_URL}${loginRegisterData.apple_store_icon.img.data.attributes.url}`
                          : AppleStoreBadge}
                        alt={translate("Download on the App Store", "حمّل من متجر آبل")}
                        className="play_store cursor-pointer h-[40px]"
                      />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=bh.solidarity.app&hl=en&gl=US"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={loginRegisterData?.google_play_icon?.img?.data?.attributes?.url
                          ? `${STRAPI_BASE_URL}${loginRegisterData.google_play_icon.img.data.attributes.url}`
                          : GooglePlayBadge}
                        alt={translate("Get it on Google Play", "احصل عليه من جوجل بلاي")}
                        className="app_store cursor-pointer h-[40px]"
                      />
                    </a>
                  </div>
                  <div className="terms_cont flex align-middle max767:block mt-4">
                    <p className="terms_cont-title mt-0 text-[#1A2024] rtl:text-right text-[14px] not-italic font-normal leading-6 tracking-[-0.14px]">
                      <a
                        href="https://www.solidarity.com.bh/termsofuse"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00384D] text-[14px] not-italic font-bold leading-[26px] tracking-[-0.14px] underline"
                      >
                        {translate(
                          loginRegisterData?.terms_conditions_link?.label_en || "Terms & conditions",
                          loginRegisterData?.terms_conditions_link?.label_ar || "شروط وأحكامنا"
                        )}
                      </a>{" "}
                      <span className="text-[#1A2024]">{translate("and", "و")}</span>{" "}
                      <a
                        href="https://www.solidarity.com.bh/privacyandsecurity"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00384D] text-[14px] not-italic font-bold leading-[26px] tracking-[-0.14px] underline"
                      >
                        {translate(
                          loginRegisterData?.privacy_policy_link?.label_en || "Privacy Policy",
                          loginRegisterData?.privacy_policy_link?.label_ar || "سياسة الخصوصية الخاصة بنا"
                        )}
                      </a>
                    </p>
                  </div>
                  <div className="terms_cont flex items-center max-[767px]:block pt-2">
                    <p className="terms_cont-title mt-0 text-left text-[#5B6871] rtl:text-right rtl:hidden text-[14px] not-italic font-normal leading-[20px] tracking-[-0.14px]">
                      Copyright ©2025 Solidarity Bahrain B.S.C. All Rights
                      Reserved. Solidarity Bahrain is a subsidiary of Solidarity
                      Group Holding. Licensed and regulated by the Central Bank
                      of Bahrain as a locally incorporated Takaful firm -
                      Category four.
                    </p>
                    <p className="terms_cont-title mt-0 text-left text-[#5B6871] rtl:text-right ltr:hidden text-[14px] not-italic font-normal leading-[20px] tracking-[-0.14px]">
                      حقوق الطبع والنشر ©2025 شركة سوليدرتي البحرين ش.م.ب. جميع
                      الحقوق محفوظة. سوليدرتي البحرين هي شركة تابعة لمجموعة
                      سوليدرتي القابضة. مرخصة ومنظمة من قبل مصرف البحرين المركزي
                      كشركة تكافل مؤسسة محليًا - الفئة الرابعة
                    </p>
                  </div>
                </div>
              )}
              {mobile && (
                <div className="flex align-center mb-2">
                  <p className="mb-3">{t("siginPage.new_to_label")} </p>
                  <Link
                    to="/sign-up"
                    className="text-[#00384D] text-base not-italic font-semibold leading-[26px] tracking-[-0.16px] underline ml-1"
                  >
                    {" "}
                    {t("siginPage.signup_link_label")}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    } else if (currentPage == ADDRESS_DETAILS_PAGE) {
      return <AddressDetailsPage onSuccess={addressDetailsOnSuccess} />;
    }
  };

  // Start the PKCE generation process when the component mounts
  useEffect(() => {}, []);

  return (
    <Modal show={showModal}>
      <Header
        isPopup
        /*
         * On the sign-in step itself the phone already has a control in the
         * top-left — BackBtnWithLabel, which navigates to "/" exactly as this
         * close does — so the header's X in the top-right was a second button
         * doing the same job, one in each corner.
         *
         * Scoped to that step and no further. Past it the user is
         * half-authenticated and this close is not equivalent to anything
         * else on screen: it calls performLogout below. GetVerifiedPage's own
         * back button only navigates away, which would leave the withheld
         * session recoverable, and AddressDetailsPage deliberately has no
         * in-page back link at all — the X is its only way out.
         */
        hideMobileClose={currentPage === SIGN_IN_PAGE}
        onCloseModal={() => {
          // Past the sign-in form the user is half-authenticated: the token was
          // withheld by cbVerifyStatus pending verification. Abandoning either
          // gate must drop that session rather than leave it recoverable.
          if (currentPage !== SIGN_IN_PAGE) {
            performLogout({ dispatch, navigate });
            return;
          }
          startTransition(() => {
            setShowModal(false);
            navigate("/");
          });
        }}
      />

      <motion.div
          key={currentPage}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full"
        >
          {togglePages()}
        </motion.div>

      {ekeyError && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl p-6 mx-4 max-w-[400px] w-full shadow-xl text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-[#1A2024] mb-2">
              {translate("Service Unavailable", "الخدمة غير متاحة")}
            </h3>
            <p className="text-sm text-[#5B6871] mb-6">
              {translate(
                "The eKey service is currently unavailable. Please try again or log in using your CPR and password instead.",
                "خدمة المفتاح الإلكتروني غير متاحة حالياً. يرجى المحاولة مرة أخرى أو تسجيل الدخول باستخدام الرقم الشخصي وكلمة المرور."
              )}
            </p>
            <button
              className="w-full py-3 bg-[#00A9E7] text-white font-bold text-base rounded-[24px] hover:bg-[#0090c4] transition-colors mb-3"
              onClick={() => {
                setEkeyError(false);
                handleEkeyLogin();
              }}
            >
              {translate("Try Again", "حاول مرة أخرى")}
            </button>
            <button
              className="w-full py-3 bg-[#F6F8F9] text-[#1A2024] font-bold text-base rounded-[24px] hover:bg-[#E8ECEE] transition-colors"
              onClick={() => setEkeyError(false)}
            >
              {translate("Login with CPR & Password", "الدخول بالرقم الشخصي وكلمة المرور")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SignInPage;
